import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { logWarn } from "../lib/log";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Send, Loader2, Bot } from "lucide-react";
import { toast } from "sonner";

export default function SaleThread({ thread, meId, onUpdated, compact = false }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [t, setT] = useState(thread);
  const endRef = useRef(null);

  useEffect(() => { setT(thread); }, [thread]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [t?.messages?.length]);
  const onUpdatedRef = useRef(onUpdated); onUpdatedRef.current = onUpdated;
  const threadId = t?.thread_id;
  useEffect(() => {
    if (!threadId) return;
    const id = setInterval(async () => {
      try { const { data } = await api.get(`/sales/threads/${threadId}`); setT(data); onUpdatedRef.current?.(data); }
      catch (e) { logWarn("sale thread poll failed", e?.response?.status || e?.message); }
    }, 5000);
    return () => clearInterval(id);
  }, [threadId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sales/threads/${t.thread_id}/messages`, { text });
      setT(data); onUpdated?.(data); setText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); } finally { setBusy(false); }
  };

  if (!t) return null;
  return (
    <div data-testid={`sale-thread-${t.thread_id}`} className="flex flex-col">
      <div className={`space-y-2 overflow-y-auto pr-1 ${compact ? "max-h-[260px]" : "max-h-[380px]"}`}>
        {t.messages.length === 0 && <p className="text-xs text-slate-500 text-center py-4">Say hi — ask about history, test drives, or make an offer.</p>}
        {t.messages.map((m) => {
          const mine = m.author_id === meId;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`sale-msg-${m.message_id}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-amber-500 text-slate-950 rounded-br-sm" : "bg-slate-800 text-slate-100 rounded-bl-sm"}`}>
                <p className={`text-[10px] font-mono mb-0.5 flex items-center gap-1 ${mine ? "text-slate-800" : "text-slate-400"}`}>
                  {m.author_name} · {m.author_role}{m.ai_generated && <Bot className="w-3 h-3" />}
                </p>
                <p className="whitespace-pre-wrap">{m.text}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 mt-3">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="sale-msg-input" placeholder="Message…" rows={1}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
          className="bg-slate-900 border-slate-800 min-h-[42px] resize-none" />
        <Button type="submit" disabled={busy || !text.trim()} data-testid="sale-msg-send" className="h-[42px] bg-white text-slate-950 hover:bg-slate-100">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}
